import type { HelpArticle } from "../../types";
import { SECTION_PROPERTIES_HELP_FIXTURES } from "../../../calculations/structuralHelpFixtures";

const rectangle = SECTION_PROPERTIES_HELP_FIXTURES.rectangle;
const iSection = SECTION_PROPERTIES_HELP_FIXTURES.iSection;
const n3 = (value: number): string => value.toLocaleString(undefined, { maximumFractionDigits: 3 });

export const sectionPropertiesArticle: HelpArticle = {
  id: "section-properties",
  title: "Section properties",
  description:
    "Calculate centroidal area, centroid, second moments, section moduli, and radii of gyration for five symmetric parametric shapes.",
  tags: ["section properties", "area", "centroid", "moment of inertia", "section modulus", "radius of gyration", "geometry"],
  lastUpdated: "July 20, 2026",
  published: false,
  blocks: [
    {
      kind: "lead",
      text: "Section Properties is a geometric analysis sheet, not a material database or strength check. Choose one of five symmetric parametric shapes, enter the dimensions that appear, and the engine returns centroidal `A`, `Ix`, `Iy`, `Sx`, `Sy`, `rx`, and `ry`. The result status is Calculated. It carries no pass/fail verdict, design method, load combination, or material capacity.",
    },

    { kind: "heading", level: 2, text: "Create a parametric section", id: "create" },
    { kind: "where", path: ["Calculations", "Project", "New calculation", "Structural calculators", "Analysis", "Section Properties"] },
    {
      kind: "paragraph",
      text: "Select Section Properties from the Analysis family. Firmark creates an `SP` sheet with a 6 × 12 in rectangle as the default. This calculator has no catalog bindings, so there is no Browse database action and no stable material key to resolve. The dimensions on the sheet are the complete input. Rename the mark if the section belongs to a detail or calculation sequence, then commit a number by leaving the field or pressing Enter.",
    },
    {
      kind: "shot",
      shotId: "structural-catalog-analysis",
      caption: "The Analysis family contains the parametric Section Properties sheet.",
    },
    { kind: "registry-group", calculatorId: "section_properties", groupId: "geometry" },
    {
      kind: "table",
      caption: "Shape choice controls which dimensions remain visible.",
      headers: ["Shape", "Visible dimensions"],
      rows: [
        ["Rectangle", "Overall width, overall depth"],
        ["Solid circle", "Outside diameter"],
        ["Hollow rectangle", "Overall width, overall depth, wall thickness"],
        ["Hollow circle", "Outside diameter, wall thickness"],
        ["I-section", "Overall depth, flange width, flange thickness, web thickness"],
      ],
    },
    {
      kind: "paragraph",
      text: "Changing Shape commits immediately and removes dimensions that do not apply from the visible form. Hidden values may remain in the stored input object, but the engine reads only the dimensions required by the selected shape. The schematic changes with the selection, so the figure beside the fields is a quick check that width, depth, flange, web, and thickness have been assigned to the intended directions.",
    },
    {
      kind: "shot",
      shotId: "section-properties-rectangle",
      caption: `${rectangle.mark}: the default rectangle exposes only width and depth, alongside the matching schematic.`,
    },

    { kind: "heading", level: 2, text: "What the engine returns", id: "results" },
    {
      kind: "definition-list",
      items: [
        { term: "A", definition: "Cross-sectional area in square inches." },
        { term: "cx, cy", definition: "Centroid coordinates measured from the section's lower-left bounding-box corner." },
        { term: "Ix, Iy", definition: "Second moments of area about centroidal x and y axes, in in⁴." },
        { term: "Sx, Sy", definition: "Elastic section moduli to the extreme fiber, in in³." },
        { term: "rx, ry", definition: "Radii of gyration, calculated from the matching centroidal second moment and area." },
      ],
    },
    {
      kind: "paragraph",
      text: `The coordinate origin is the lower-left corner of the shape's overall bounding box, while the reported x and y axes pass through the centroid. For ${rectangle.mark}, symmetry places cx at ${n3(rectangle.values.cx_in)} in and cy at ${n3(rectangle.values.cy_in)} in. The 12 in depth produces the larger Ix and Sx; the 6 in width produces Iy and Sy. That axis convention matters when a property is copied into another calculation: x describes bending across the entered depth, and y describes bending across the entered width.`,
    },
    {
      kind: "formula",
      expression: "r_x = √(I_x / A)    ·    r_y = √(I_y / A)",
      caption: "The radius values use the same centroidal axis labels as the reported second moments.",
    },
    {
      kind: "paragraph",
      text: "The right pane treats these as property metrics and a detailed result table, not as demand/capacity cards. The governing banner says Section calculated, and the header says Calculated without a utilization percentage. Detailed mode exposes the geometric method, intermediate values, source statement, and limitations. Quick mode keeps the primary property result while collapsing that trace; it does not change the values.",
    },
    {
      kind: "shot",
      shotId: "section-properties-rectangle-results",
      caption: `${rectangle.mark}: the primary centroidal metric strip for a 6 × 12 in rectangle.`,
    },
    {
      kind: "values",
      caption: `${rectangle.mark} · 6 × 12 in solid rectangle.`,
      rows: [
        { label: "A", value: `${n3(rectangle.values.area_in2)} in²` },
        { label: "cx · cy", value: `${n3(rectangle.values.cx_in)} · ${n3(rectangle.values.cy_in)} in` },
        { label: "Ix · Iy", value: `${n3(rectangle.values.ix_in4)} · ${n3(rectangle.values.iy_in4)} in⁴` },
        { label: "Sx · Sy", value: `${n3(rectangle.values.sx_in3)} · ${n3(rectangle.values.sy_in3)} in³` },
        { label: "rx · ry", value: `${n3(rectangle.values.rx_in)} · ${n3(rectangle.values.ry_in)} in` },
      ],
    },

    { kind: "heading", level: 2, text: "Reading an I-section", id: "i-section" },
    {
      kind: "paragraph",
      text: "The I-section is doubly symmetric. Overall depth sets the y extent, flange width sets the x extent, and the web is centered between equal top and bottom flanges. The engine applies the parallel-axis theorem to the two flanges and adds the web contribution. There are no fillet radii or flange tapers in the parametric model, so this shape is useful for idealized geometry rather than a substitute for a rolled-shape database row.",
    },
    {
      kind: "values",
      caption: `${iSection.mark} · 12 in deep, 8 in flange, 0.75 in flange thickness, 0.5 in web.`,
      rows: [
        { label: "A", value: `${n3(iSection.values.area_in2)} in²` },
        { label: "cx · cy", value: `${n3(iSection.values.cx_in)} · ${n3(iSection.values.cy_in)} in` },
        { label: "Ix · Iy", value: `${n3(iSection.values.ix_in4)} · ${n3(iSection.values.iy_in4)} in⁴` },
        { label: "Sx · Sy", value: `${n3(iSection.values.sx_in3)} · ${n3(iSection.values.sy_in3)} in³` },
        { label: "rx · ry", value: `${n3(iSection.values.rx_in)} · ${n3(iSection.values.ry_in)} in` },
      ],
    },
    {
      kind: "shot",
      shotId: "section-properties-i-section-results",
      caption: `${iSection.mark}: entered dimensions and schematic beside the visible A, centroid, Ix, Iy, and Sx summary.`,
    },
    {
      kind: "paragraph",
      text: "A hollow rectangle or hollow circle subtracts the inside void from the matching solid shape. The runtime therefore requires positive inside dimensions. An I-section must leave positive web depth between the flanges, and web thickness must remain below flange width. These geometry gates fail the recalculation with the engine's error rather than returning a plausible-looking property set for an impossible section.",
    },

    { kind: "heading", level: 2, text: "What this result does not establish", id: "limits" },
    {
      kind: "callout",
      variant: "warning",
      title: "Calculated is not approved or adequate",
      body: "The sheet establishes geometric properties only. It does not select material, check compactness or slenderness, calculate torsion constants, apply effective-width rules, compare demand with capacity, or establish a code design strength.",
    },
    {
      kind: "paragraph",
      text: "The v1 shape library is limited to symmetric rectangles, circles, tubes, and doubly symmetric I-sections. Fillets, corner radii, fabrication tolerances, unsymmetric shapes, principal-axis rotation, plastic modulus, warping, and torsion need another source or analysis. Export preserves the Calculated status and the property trace so the sheet cannot be mistaken for a passing member design.",
    },

    { kind: "related", topics: ["steel-cfs-sheets", "concrete-foundation-sheets", "masonry-sheets"] },
  ],
};
